import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Mail, Send, Check, X, Clock, ArrowRight } from 'lucide-react';

export default function InvitesPage() {
  const [data, setData] = useState({ incoming: [], outgoing: [] });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const fetchInvites = () => {
    api.get('/clubs/my-invites')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchInvites(); }, []);

  const handleAccept = async (id) => {
    setActing(id);
    try {
      const res = await api.post(`/clubs/invites/${id}/accept`);
      toast.success(res.data.message);
      fetchInvites();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActing(null); }
  };

  const handleReject = async (id) => {
    setActing(id);
    try {
      await api.post(`/clubs/invites/${id}/reject`);
      toast.success('Invite declined');
      fetchInvites();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActing(null); }
  };

  const statusBadge = (status) => {
    const map = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      accepted: 'bg-green-100 text-green-700 border-green-200',
      approved: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-600 border-red-200',
    };
    return `text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${map[status] || 'bg-slate-100 text-slate-500 border-slate-200'}`;
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="invites-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Invites & Requests</h1>
        <p className="text-slate-500 mt-1 text-sm">Manage club invitations and join requests</p>
      </div>

      {/* Incoming Club Invites */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="incoming-invites">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
          <Mail size={16} className="text-green-600" />
          <h2 className="text-sm font-bold text-slate-900">Club Invitations</h2>
          <span className="text-xs text-slate-400 ml-auto">{data.incoming.filter(i => i.status === 'pending').length} pending</span>
        </div>
        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-slate-400">Loading...</div>
        ) : data.incoming.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-400">No club invitations yet</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.incoming.map(inv => (
              <div key={inv.id} className="px-5 py-3.5 flex items-center gap-3" data-testid={`invite-${inv.id}`}>
                <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                  <Mail size={16} className="text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{inv.club_name}</p>
                  {inv.message && <p className="text-xs text-slate-500 mt-0.5 truncate">{inv.message}</p>}
                  <p className="text-[10px] text-slate-400 mt-0.5">{inv.created_at}</p>
                </div>
                {inv.status === 'pending' ? (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => handleAccept(inv.id)} disabled={acting === inv.id}
                      className="h-7 px-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white text-xs font-semibold rounded-lg transition-all"
                      data-testid={`accept-invite-${inv.id}`}>
                      <Check size={12} />
                    </button>
                    <button onClick={() => handleReject(inv.id)} disabled={acting === inv.id}
                      className="h-7 px-3 bg-white border border-slate-200 hover:bg-red-50 hover:border-red-200 text-slate-500 hover:text-red-600 text-xs font-semibold rounded-lg transition-all"
                      data-testid={`reject-invite-${inv.id}`}>
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <span className={statusBadge(inv.status)}>{inv.status}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Outgoing Join Requests */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="outgoing-requests">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
          <Send size={16} className="text-blue-600" />
          <h2 className="text-sm font-bold text-slate-900">My Join Requests</h2>
        </div>
        {data.outgoing.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-400">No join requests sent</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.outgoing.map(req => (
              <div key={req.id} className="px-5 py-3.5 flex items-center gap-3" data-testid={`request-${req.id}`}>
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Send size={16} className="text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{req.club_name}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{req.created_at}</p>
                </div>
                <span className={statusBadge(req.status)}>{req.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
